import { Component, Inject, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Driver,
  InterestedDriver,
  JourneyInterestedDriver,
  RideRequestDetail,
  RideRequestService,
  RideUtil,
  VehicleType,
  Provider
} from '@uzurv/clara';
import { VehicleTypeConstants, isNullOrUndefined } from '@uzurv/core';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

export interface SelectDriverDialogData {
  vehicleType: VehicleType;
  rideRequest: RideRequestDetail;
}

@Component({
  selector: 'app-select-driver-dialog',
  templateUrl: './select-driver-dialog.component.html',
  styleUrls: ['./select-driver-dialog.component.scss']
})
export class SelectDriverDialogComponent implements OnChanges {
  private _interestedDrivers: Array<InterestedDriver>;
  private _journeyInterestedDrivers: Array<JourneyInterestedDriver>;
  private _providersIgnoringRideRequest: Array<Provider>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectDriverDialogData,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<SelectDriverDialogComponent>,
    private rideRequestService: RideRequestService
  ) {
    this.populateDialog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.populateDialog();
    }
  }

  private populateDialog() {
    if (!isNullOrUndefined(this.data.rideRequest)) {
      if (this.isJourneyEligible()) {
        this.fetchJourneyInterestedDrivers();
      } else {
        this.fetchInterestedDrivers();

        if (this.isWavRide()) {
          this.fetchProvidersIgnoringRideRequest();
        }
      }
    }
  }

  get interestedDrivers(): Array<InterestedDriver> {
    return this._interestedDrivers;
  }

  get journeyInterestedDrivers(): Array<JourneyInterestedDriver> {
    return this._journeyInterestedDrivers;
  }

  get providersIgnoringRideRequest(): Array<Provider> {
    return this._providersIgnoringRideRequest;
  }

  close(): void {
    this.dialogRef.close();
  }

  handleInterestedDriverSelected(interestedDriver: InterestedDriver) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are You Sure?',
          message: `
            Are you sure you want to select ${interestedDriver.driver.firstName} ${
            interestedDriver.driver.lastName ? interestedDriver.driver.lastName : ''
          } for this reservation?
            ${!interestedDriver.onWavPlatform ? '<br />Please confirm with the provider.' : ''}
            `,
          isDestructive: false
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({
            interestedDriver: interestedDriver
          });
        }
      });
  }

  // Ride Requests

  private fetchInterestedDrivers() {
    if (isNullOrUndefined(this.data?.rideRequest)) {
      return;
    }

    this.rideRequestService.getInterestedDriversByRideRequestId(this.data.rideRequest.id).subscribe(
      interestedDrivers => {
        this._interestedDrivers = interestedDrivers;
      },
      err => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Error',
            message: err.message
          }
        });
      }
    );
  }

  // WAV

  isWavRide(): boolean {
    return this.data?.vehicleType?.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV;
  }

  private fetchProvidersIgnoringRideRequest() {
    if (isNullOrUndefined(this.data?.rideRequest)) {
      return;
    }

    this.rideRequestService.listProvidersIgnoringRideRequestById(this.data.rideRequest.id).subscribe(
      providersIgnoringRideRequest => {
        this._providersIgnoringRideRequest = providersIgnoringRideRequest;
      },
      err => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Error',
            message: err.message
          }
        });
      }
    );
  }

  getRequestedTimes(interestedDrivers: Array<InterestedDriver>): Array<InterestedDriver> {
    if (this.isWavRide()) {
      return interestedDrivers.filter(interestedDriver => !interestedDriver.proposedPickupTime && interestedDriver.onWavPlatform);
    }

    return interestedDrivers;
  }

  getProposedTimes(interestedDrivers: Array<InterestedDriver>): Array<InterestedDriver> {
    if (this.isWavRide()) {
      return interestedDrivers.filter(interestedDriver => interestedDriver.proposingNewPickupTime && interestedDriver.onWavPlatform);
    }

    return null;
  }

  getNotOnPlatformInterest(interestedDrivers: Array<InterestedDriver>): Array<InterestedDriver> {
    if (this.isWavRide()) {
      return interestedDrivers.filter(interestedDriver => !interestedDriver.onWavPlatform);
    }

    return null;
  }

  convertProviderToInterestedDriver(provider: Provider): InterestedDriver {
    const interestedDriver = new InterestedDriver();
    interestedDriver.driver = new Driver();
    interestedDriver.driver.firstName = provider.name;
    interestedDriver.driver.lastName = '';
    interestedDriver.driver.emailAddress = provider.emailAddress;
    interestedDriver.driver.phoneNumber = provider.phoneNumber;
    interestedDriver.driver.vehicles = [];

    return interestedDriver;
  }

  // Journey

  isJourneyEligible(): boolean {
    return RideUtil.isRideRequestDetailJourneyEligible(this.data?.rideRequest);
  }

  private fetchJourneyInterestedDrivers() {
    if (isNullOrUndefined(this.data?.rideRequest)) {
      return;
    }

    this.rideRequestService.listJourneyInterestedDriversByRideRequestId(this.data.rideRequest.id).subscribe(
      journeyInterestedDrivers => {
        this._journeyInterestedDrivers = journeyInterestedDrivers;
      },
      err => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Error',
            message: err.message
          }
        });
      }
    );
  }

  handleJourneyInterestedDriverSelected(journeyInterestedDriver: JourneyInterestedDriver) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are You Sure?',
          message: `
            Are you sure you want to select ${journeyInterestedDriver.driver.firstName} ${
            journeyInterestedDriver.driver.lastName ? journeyInterestedDriver.driver.lastName : ''
          } for this reservation?`,
          isDestructive: false
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({
            journeyInterestedDriver: journeyInterestedDriver
          });
        }
      });
  }
}
